import type { CodeRepositoryRef } from '@/data/code-sessions/environment';
/**
 * View-model types for the Code page. The `SessionPart` union lives in
 * `transcript-model.ts` next to the fold that produces it; this file keeps the
 * session list/transcript view types and small accessors.
 */
import type { SessionCapabilities, SessionConnection } from '@/data/code-sessions/runtime/types';
import type { TurnActivity } from '@/data/code-sessions/turn-activity';
import type { CodeSessionRow } from '@/data/code-sessions/types';
import type { AiSubscriptionSessionView } from '@/lib/code-sessions/ai-subscriptions/policy';
import type { ApprovalMode } from '@/lib/code-sessions/approval-mode';
import type { CodeSessionOrigin } from '@/lib/code-sessions/origin';
import type { CodeSessionPullRequest } from '@/lib/code-sessions/pull-request';
import type { CodeSessionPerson, CodeSessionProfile } from '@/lib/code-sessions/session-people';

import type { SessionPart, TraceStep, TraceStepKind } from './transcript-model';

import { savedOrigin } from '@/lib/code-sessions/origin';

export type { SessionPart, TraceStep, TraceStepKind };

export const SESSION_STATUS = {
  RUNNING: 'running',
  DONE: 'done',
  NEEDS_INPUT: 'needs_input',
  FAILED: 'failed',
} as const;

export type SessionStatus = (typeof SESSION_STATUS)[keyof typeof SESSION_STATUS];

export const FILE_CHANGE_STATUS = {
  ADDED: 'added',
  MODIFIED: 'modified',
  DELETED: 'deleted',
  RENAMED: 'renamed',
} as const;

export type FileChangeStatus = (typeof FILE_CHANGE_STATUS)[keyof typeof FILE_CHANGE_STATUS];

export type SessionFileChange = {
  path: string;
  previousPath?: string;
  status: FileChangeStatus;
  additions: number;
  deletions: number;
  /** The saved turn could not count this file's lines, so its totals here read as 0. */
  totalsIncomplete?: boolean;
};

export type SessionWorkspace = {
  files: SessionFileChange[];
};

export const PRESENCE_STATUS = {
  ONLINE: 'online',
  IDLE: 'idle',
} as const;

export type PresenceStatus = (typeof PRESENCE_STATUS)[keyof typeof PRESENCE_STATUS];

export const PRESENCE_ACTIVITY = {
  VIEWING: 'viewing',
  EDITING: 'editing',
} as const;

export type PresenceActivity = (typeof PRESENCE_ACTIVITY)[keyof typeof PRESENCE_ACTIVITY];

/** Someone else looking at the same session. Mock-only; no realtime transport. */
export type SessionOccupant = {
  id: string;
  name: string;
  status: PresenceStatus;
  /** What they are doing right now; omitted while idle. */
  activity?: PresenceActivity;
  /** Tailwind classes for the initials fallback so the stack reads as distinct people. */
  tint: string;
};

export type CodeSession = {
  id: string;
  title: string;
  repo: string;
  /** The session's repository as the environment API identifies it; `fullName` repeats `repo`. */
  repositoryRef: CodeRepositoryRef;
  branch: string;
  /** OpenCode model ref, e.g. `vercel/openai/gpt-6-luna`; unset where the view does not know it. */
  model?: string;
  /**
   * Tokens the agent's context holds now (`contextTokens`); unset when no reply was measured since
   * the conversation was last compacted, and where the view does not read it.
   */
  contextTokens?: number;
  /**
   * The approval mode the session runs in; unset where the view does not read the session's rules,
   * and the composer shows no picker for a started session then.
   */
  approvalMode?: ApprovalMode;
  status: SessionStatus;
  /** Provisional session while creation is awaiting the saved session view. */
  creating?: boolean;
  /**
   * The key the sidebar lists the session under, where that is not its id. A session created on
   * this page keeps the key of the placeholder it replaces (`listKeyOf`).
   */
  listKey?: string;
  /** For a side agent, the main session it works beside; unset on sidebar list items. */
  sideAgentParentId?: string | null;
  /**
   * What the session's row lets the client do (`capabilitiesOf`). A sidebar list item and a
   * placeholder carry few row fields, which read as a main session that is not setup-only.
   */
  can: SessionCapabilities;
  /**
   * The base64 bytes of images the open session can still store, where its prompts take only
   * images (`SessionCapabilities.imagesOnly`). It is the session response's `imageRoom`, which the
   * runtime on the person's own computer counts (`localImageRoom`) from the images of its first
   * prompt until its first read, and counting the images of sends OpenCode never got, which their
   * Retry sends again. Unset elsewhere, and for a session about to start, which stores no images
   * yet.
   */
  imageRoom?: number;
  /** Archived: the session takes no prompts until it is unarchived. */
  archived?: boolean;
  /**
   * Its status is past startup: not creating, and not failed to start. The
   * server runs a setup script again only in a started session.
   */
  started: boolean;
  /**
   * Unarchive and Undo restore the session at once, its sidebar entry and its page, while the
   * server confirms the change.
   */
  restoring?: boolean;
  /**
   * File and diff reads wait for the existing session-resume flow. Set two ways:
   * on a sidebar list item, when the row's sandbox is ready (hovering the row
   * prefetches the session only then); on the open session, when its sandbox is
   * ready and it is not resuming.
   */
  workspaceReady?: boolean;
  /**
   * Compact relative label (`4m`, `1d`); static so the prototype matches on server and client.
   * The sidebar shows it only for a session without `lastActivityAt`.
   */
  when: string;
  /**
   * Saved `lastActivityAt` from the session list, for comparing against cached detail. The
   * sidebar measures its last-activity label from it against the minute tick.
   */
  lastActivityAt?: string;
  /**
   * The branch's PR: an opened session's live GitHub lookup, else the one the
   * session list read from the webhook-fed mirror. Undefined when neither has
   * answered, null when no PR matches.
   */
  pullRequest?: CodeSessionPullRequest | null;
  /** The session finished or asked something since the user last opened it. */
  unread?: boolean;
  /** The session this one was forked from; its title is unknown when the list does not hold it. */
  forkedFrom?: { sessionId: string; title?: string };
  /** The session whose agent started this one; its title is unknown when the list does not hold it. */
  startedBy?: { sessionId: string; title?: string };
  /**
   * Where the session was started, when that was outside the web app: from a Slack message (its
   * thread's link when Slack gave one), by cubic when its pull request opened, to film a demo of
   * it, or over MCP by an app the person connected (its name when it registered one).
   */
  startedOutside?: Exclude<CodeSessionOrigin, { source: 'web' }>;
  /** The codebase scan finding the session is a conversation about, whose card heads it. */
  findingId?: string;
  /**
   * Runs on this computer, independently of where it was started: in a folder, whose name `repo`
   * carries. Set from the row's id, and on a session starting in a folder before it has one.
   */
  onThisComputer?: boolean;
  /**
   * Who started the session; null when no profile is theirs, as for a session cubic owns.
   * Unset where the view has not read it.
   */
  createdBy?: CodeSessionProfile | null;
  /**
   * Who started the session, by profile ID. Unset on a row a window on older code saved to the
   * query cache, and on creation placeholder rows. Read it through `sessionOwner`; nothing else
   * compares it with the viewer.
   */
  createdByProfileId?: string;
  /**
   * The open session's people: who started it, then each other person who wrote in it. Unset on
   * sidebar list items, and until the session's read names them.
   */
  people?: readonly CodeSessionPerson[];
  /**
   * The boundary turn of a rewind waiting for its next prompt or Discard: the
   * open session's own with a pending edit applied, else as the list read it.
   */
  rewindTurnId?: string | null;
  /** When the pending scheduled check-in is due, if one is. */
  checkInDueAt?: string;
  /** The agents still at work on a task it sent (`CodeSessionSummary.agentsAtWork`); unset when none. */
  agentsAtWork?: string[];
  /** Whose own MCP accounts only their messages use, shown to members who did not create the session. */
  mcpToolsRunAs?: string | null;
  /**
   * Whose AI subscription the session runs on, as its read says; null for a session on cubic's
   * Gateway. Unset where the view has not read it.
   */
  aiSubscription?: AiSubscriptionSessionView | null;
  /** Changed files the Review panel reads. */
  workspace?: SessionWorkspace;
  /** The open session's live feed, once down past the grace (`useDisconnectDelay`); unset reads as live. */
  connection?: SessionConnection;
  parts: SessionPart[];
  /** Local dispatch and server turn status share one work indicator. */
  activity?: TurnActivity[];
};

/** `CodeSession.startedOutside` for a session started outside the web app. */
export function sessionOrigin(
  row: Pick<CodeSessionRow, 'source' | 'sourceUrl' | 'mcpClientName'>,
): Pick<CodeSession, 'startedOutside'> {
  const origin = savedOrigin(row);
  return origin === null || origin.source === 'web' ? {} : { startedOutside: origin };
}

export function listKeyOf(session: Pick<CodeSession, 'id' | 'listKey'>): string {
  return session.listKey ?? session.id;
}

/** Whether a saved session's status is past startup, for `CodeSession.started`. */
export function hasStarted(status: CodeSessionRow['status']): boolean {
  return status !== 'creating' && status !== 'failed';
}

export function getSessionOccupants(_session: CodeSession): SessionOccupant[] {
  // Presence is M3; no realtime transport yet.
  return [];
}

export function getSessionFileChanges(session: CodeSession): SessionFileChange[] {
  return session.workspace?.files ?? [];
}

export function getSessionDiffTotals(session: CodeSession): {
  additions: number;
  deletions: number;
} {
  return getSessionFileChanges(session).reduce(
    (totals, file) => ({
      additions: totals.additions + file.additions,
      deletions: totals.deletions + file.deletions,
    }),
    { additions: 0, deletions: 0 },
  );
}

export function hasSessionReview(_session: CodeSession): boolean {
  // Review panel is M3; hide it until the workspace/PR flow is wired.
  return false;
}

/** Whether a sidebar search matches the session's title, saved or current repository, or branch. */
export function sessionMatchesQuery(
  session: CodeSession,
  query: string,
  canonicalRepo: string = session.repo,
): boolean {
  const normalized = query.trim().toLowerCase();
  if (normalized.length === 0) {
    return true;
  }

  return [session.title, session.repo, canonicalRepo, session.branch].some((term) =>
    term.toLowerCase().includes(normalized),
  );
}
